import React from "react";
import TitleHelmet from "@/components/TitleHelmet";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Container, Card, Row, Col, Button, Spinner, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ClockCircleOutlined, MessageOutlined, ExclamationCircleOutlined, PlayCircleOutlined, WifiOutlined, GlobalOutlined, ShoppingCartOutlined, InboxOutlined, TruckOutlined, HomeOutlined, CustomerServiceOutlined } from "@ant-design/icons";
import PageTitle from "@/common/PageTitle/PageTitle";
import { initialOrders, getOrderStats } from '@/data/orders';
import { useGetSupportAttentionQuery } from "@/features/accounts/accountsApi";
import styles from './dashboard.module.scss';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const {
    data: supportAttention,
    isFetching: supportAttentionLoading,
    isError: supportAttentionError,
  } = useGetSupportAttentionQuery({ maxItems: 500, clientLimit: 5 });

  // Mock data for pending approvals
  const pendingApprovals = {
    musicVendors: 3,
    installerVendors: 2,
    musicVendorRoles: 1,
    installerVendorRoles: 0
  };

  // Mock data for pending message requests
  const pendingMessageRequests = {
    urgent: 2,
    standard: 5,
    total: 7
  };

  // Mock data for live streaming status
  const liveStreamingStatus = {
    activeLocations: 47,
    totalLocations: 52,
    onlineDevices: 89,
    totalDevices: 95,
    streamingHours: 1247
  };

  // Get real order statistics
  const orderStats = getOrderStats(initialOrders);
  const incomingOrders = {
    newLocations: orderStats.pending, // Use actual pending orders count
    deviceOrders: orderStats.labelCreated, // Orders with labels created
    accessoryOrders: orderStats.shipped, // Shipped/delivered orders
    totalOrders: orderStats.totalItems // Total items in pending orders
  };

  return (
    <Container fluid>
      <Card className={styles.card}>
        <Card.Body>
          <TitleHelmet title="Dashboard" />
          <Card.Title className={styles.cardTitle}>
            <PageTitle title="Dashboard" />
          </Card.Title>
          
          {/* Live Streaming Status Widget */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className={styles.liveStreamingCard}>
                <Card.Body>
                  <div className={styles.liveStreamingHeader}>
                    <h5 className={styles.liveStreamingTitle}>
                      <PlayCircleOutlined className={styles.streamingIcon} />
                      Live Streaming Status
                    </h5>
                    <div className={styles.lastUpdated}>
                      Last updated: {new Date().toLocaleTimeString()}
                    </div>
                  </div>
                  <Row>
                    <Col md={3}>
                      <div className={styles.streamingItem}>
                        <div className={styles.streamingCount}>{liveStreamingStatus.activeLocations}</div>
                        <div className={styles.streamingLabel}>
                          <GlobalOutlined className={styles.locationIcon} />
                          Active Locations
                        </div>
                        <div className={styles.streamingSubtext}>
                          of {liveStreamingStatus.totalLocations} total
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className={styles.streamingItem}>
                        <div className={styles.streamingCount}>{liveStreamingStatus.onlineDevices}</div>
                        <div className={styles.streamingLabel}>
                          <WifiOutlined className={styles.deviceIcon} />
                          Online Devices
                        </div>
                        <div className={styles.streamingSubtext}>
                          of {liveStreamingStatus.totalDevices} total
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className={styles.streamingItem}>
                        <div className={styles.streamingCount}>{liveStreamingStatus.streamingHours}</div>
                        <div className={styles.streamingLabel}>
                          <PlayCircleOutlined className={styles.hoursIcon} />
                          Streaming Hours
                        </div>
                        <div className={styles.streamingSubtext}>
                          today
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className={styles.streamingItem}>
                        <div className={styles.streamingPercentage}>
                          {Math.round((liveStreamingStatus.activeLocations / liveStreamingStatus.totalLocations) * 100)}%
                        </div>
                        <div className={styles.streamingLabel}>
                          <GlobalOutlined className={styles.uptimeIcon} />
                          Uptime
                        </div>
                        <div className={styles.streamingSubtext}>
                          locations active
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          {/* Incoming Orders Widget */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className={styles.newOrdersCard}>
                <Card.Body>
                  <div className={styles.newOrdersHeader}>
                    <h5 className={styles.newOrdersTitle}>
                      <ShoppingCartOutlined className={styles.ordersIcon} />
                      Incoming Orders
                    </h5>
                    <div className={styles.ordersActions}>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => navigate('/inventory-products')}
                        className="me-2"
                      >
                        <InboxOutlined /> Production
                      </Button>
                      <Button 
                        variant="outline-success" 
                        size="sm"
                        onClick={() => navigate('/inventory-shipping')}
                      >
                        <TruckOutlined /> Shipping
                      </Button>
                    </div>
                  </div>
                  <Row>
                    <Col md={3}>
                      <div className={styles.orderItem}>
                        <div className={styles.orderCount}>{incomingOrders.newLocations}</div>
                        <div className={styles.orderLabel}>
                          <HomeOutlined className={styles.locationIcon} />
                          Pending Orders
                        </div>
                        <div className={styles.orderSubtext}>
                          awaiting labels
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className={styles.orderItem}>
                        <div className={styles.orderCount}>{incomingOrders.deviceOrders}</div>
                        <div className={styles.orderLabel}>
                          <InboxOutlined className={styles.deviceIcon} />
                          Labels Created
                        </div>
                        <div className={styles.orderSubtext}>
                          ready to ship
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className={styles.orderItem}>
                        <div className={styles.orderCount}>{incomingOrders.accessoryOrders}</div>
                        <div className={styles.orderLabel}>
                          <InboxOutlined className={styles.accessoryIcon} />
                          Shipped/Delivered
                        </div>
                        <div className={styles.orderSubtext}>
                          in transit or completed
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className={styles.orderItem}>
                        <div className={styles.orderCount}>{incomingOrders.totalOrders}</div>
                        <div className={styles.orderLabel}>
                          <ShoppingCartOutlined className={styles.totalIcon} />
                          Total Items
                        </div>
                        <div className={styles.orderSubtext}>
                          in pending orders
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          {/* Client Support Widget */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className={styles.clientSupportCard}>
                <Card.Body>
                  <div className={styles.clientSupportHeader}>
                    <h5 className={styles.clientSupportTitle}>
                      <CustomerServiceOutlined className={styles.clientSupportIcon} />
                      Client Support
                    </h5>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => navigate("/client-support")}
                    >
                      View All
                    </Button>
                  </div>
                  {supportAttentionLoading && !supportAttention ? (
                    <div className="d-flex align-items-center gap-2 py-3">
                      <Spinner animation="border" size="sm" />
                      <span className="text-muted small">Loading…</span>
                    </div>
                  ) : supportAttentionError ? (
                    <Alert variant="warning" className="mb-0 py-2">
                      Unable to load Client Support attention.
                    </Alert>
                  ) : (supportAttention?.clientsNeedingSupport || 0) === 0 ? (
                    <p className="text-muted mb-0 py-2">
                      No Clients currently need support.
                    </p>
                  ) : (
                    <>
                      {(supportAttention?.exact === false ||
                        supportAttention?.truncated) && (
                        <Alert variant="warning" className="py-2 mb-3">
                          Counts may be partial.
                        </Alert>
                      )}
                      <Row className="mb-3">
                        <Col md={6}>
                          <div className={styles.clientSupportMetric}>
                            <div className={styles.clientSupportCount}>
                              {supportAttention?.clientsNeedingSupport ?? 0}
                            </div>
                            <div className={styles.clientSupportLabel}>
                              Clients needing support
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className={styles.clientSupportMetric}>
                            <div className={styles.clientSupportCount}>
                              {supportAttention?.unresolvedCaseCount ?? 0}
                            </div>
                            <div className={styles.clientSupportLabel}>
                              Total unresolved cases
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <div className={styles.clientSupportList}>
                        {(supportAttention?.clients || []).map((row) => (
                          <button
                            key={row.masterAccountId}
                            type="button"
                            className={styles.clientSupportListItem}
                            onClick={() =>
                              navigate(
                                `/accounts?client=${encodeURIComponent(row.masterAccountId)}&panel=support`,
                              )
                            }
                          >
                            <span className={styles.clientSupportListName}>
                              {row.businessName || row.masterAccountId}
                            </span>
                            <span className={styles.clientSupportListMeta}>
                              {row.totalNeedingAttention} open
                              {row.customerNumber
                                ? ` · ${row.customerNumber}`
                                : ""}
                            </span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Pending Message Requests Widget */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className={styles.messageRequestsCard}>
                <Card.Body>
                  <div className={styles.messageRequestsHeader}>
                    <h5 className={styles.messageRequestsTitle}>
                      <MessageOutlined className={styles.messageIcon} />
                      Pending Message Requests
                    </h5>
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => navigate('/message-requests')}
                    >
                      View All
                    </Button>
                  </div>
                  <Row>
                    <Col md={4}>
                      <div className={styles.messageItem}>
                        <div className={styles.messageCount}>{pendingMessageRequests.urgent}</div>
                        <div className={styles.messageLabel}>
                          <ExclamationCircleOutlined className={styles.urgentIcon} />
                          Urgent Requests
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className={styles.messageItem}>
                        <div className={styles.messageCount}>{pendingMessageRequests.standard}</div>
                        <div className={styles.messageLabel}>Standard Requests</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className={styles.messageItem}>
                        <div className={styles.messageCount}>{pendingMessageRequests.total}</div>
                        <div className={styles.messageLabel}>Total Requests</div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          {/* Pending Approvals Widget */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className={styles.pendingApprovalsCard}>
                <Card.Body>
                  <div className={styles.pendingApprovalsHeader}>
                    <h5 className={styles.pendingApprovalsTitle}>
                      <ClockCircleOutlined className={styles.pendingIcon} />
                      Pending Vendor Approvals
                    </h5>
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => navigate('/pending-approvals')}
                    >
                      View All
                    </Button>
                  </div>
                  <Row>
                    <Col md={3}>
                      <div className={styles.approvalItem}>
                        <div className={styles.approvalCount}>{pendingApprovals.musicVendors}</div>
                        <div className={styles.approvalLabel}>Music Vendors</div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className={styles.approvalItem}>
                        <div className={styles.approvalCount}>{pendingApprovals.installerVendors}</div>
                        <div className={styles.approvalLabel}>Installer Vendors</div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className={styles.approvalItem}>
                        <div className={styles.approvalCount}>{pendingApprovals.musicVendorRoles}</div>
                        <div className={styles.approvalLabel}>Music Vendor Roles</div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className={styles.approvalItem}>
                        <div className={styles.approvalCount}>{pendingApprovals.installerVendorRoles}</div>
                        <div className={styles.approvalLabel}>Installer Vendor Roles</div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default withAuthenticationRequired(Dashboard); 